import {inject, Injectable} from "@angular/core";
import {Observable} from "rxjs";
import {Asset} from "../models/asset/asset";
import {AssetApiService, AssetPage} from "../generated-sources/api";
import {FinancialPerformanceRecord} from "../models/asset/financial-performance-record";
import {Shareholder} from "../models/asset/shareholder";
import {AssetGeneralInfo, GptApiService} from "../../shared/generated-sources/api/gpt";
import {TraceableText} from "../../shared/model/traceable";
import {Swot} from "../models/asset/swot";
import {AssetData} from "../models/asset/asset-data";
import {AssetOverviewParams} from "../store/asset.state";
import {AssetCommentaryHistoryEntry} from "../models/asset/asset-commentary-history-entry";

@Injectable({
    providedIn: "root"
})
export class AssetService {
    private readonly assetApiService = inject(AssetApiService);
    private readonly gptApiService = inject(GptApiService);


    constructor() {
    }

    getAssets(dataset?: string, isArchived?: boolean): Observable<Asset[]> {
        return this.assetApiService.getAssets(dataset, isArchived);
    }

    searchAssets(dataset: string | undefined, params: AssetOverviewParams): Observable<AssetPage> {
        return this.assetApiService.searchAssets(
            dataset,
            params.archived,
            params.search || undefined,
            params.page,
            params.size,
            params.sort
        );
    }

    getAssetData(id: number, fundReportId?: number, fundValuationId?: number, dealId?: number, dataset?: string): Observable<AssetData> {
        return this.assetApiService.getAssetData(id, fundReportId, fundValuationId, dealId, dataset);
    }

    getAsset(id: number): Observable<Asset> {
        return this.assetApiService.getAsset(id);
    }

    saveAsset(asset: Asset, id?: number, dataset?: string): Observable<Asset> {
        return (id) ? this.assetApiService.updateAsset(id, asset) : this.assetApiService.createAsset(asset, dataset);
    }

    archiveAsset(id: number, archive: boolean): Observable<Asset> {
        return this.assetApiService.patchAsset(id, {archive});
    }

    getAssetFinancialHistory(assetId: number): Observable<FinancialPerformanceRecord[]> {
        return this.assetApiService.getHistoryForAsset(assetId);
    }

    saveAssetFinancialHistory(assetId: number, historyRecords: FinancialPerformanceRecord[]): Observable<FinancialPerformanceRecord[]> {
        return this.assetApiService.updateAssetHistory(assetId, historyRecords);
    }

    calcAssetFinancialHistory(assetId: number, historyRecords: FinancialPerformanceRecord[]): Observable<FinancialPerformanceRecord[]> {
        return this.assetApiService.calculateAssetHistory(assetId, historyRecords);
    }

    getAllShareholders(assetId: number): Observable<Shareholder[]> {
        return this.assetApiService.getAllShareholdersForAsset(assetId);
    }

    getShareholdersForDate(assetId: number, date: string): Observable<Shareholder[]> {
        return this.assetApiService.getShareholdersForAssetAndDate(assetId, date);
    }

    getCommentaryHistory(assetId: number): Observable<AssetCommentaryHistoryEntry[]> {
        return this.assetApiService.getCommentaryHistoryForAsset(assetId);
    }

    saveShareholdersForDate(assetId: number, date: string, shareholders: Shareholder[]): Observable<Shareholder[]> {
        return this.assetApiService.updateShareholdersForAsset(assetId, date, shareholders);
    }

    getGptCompanyDescription(companyName: string, websiteUrl?: string): Observable<TraceableText> {
        return this.gptApiService.getGPTCompanyDescription(companyName, websiteUrl);
    }

    getGptEndMarkets(companyName: string, websiteUrl?: string): Observable<TraceableText> {
        return this.gptApiService.getGPTEndMarketsDescription(companyName, websiteUrl);
    }

    getGptSwotAnalysis(companyName: string, websiteUrl?: string): Observable<Swot> {
        return this.gptApiService.getGPTSwotAnalysis(companyName, websiteUrl);
    }

    getGptGeneralInfo(companyName: string, websiteUrl?: string): Observable<AssetGeneralInfo> {
        return this.gptApiService.getGPTGeneralInfo(companyName, websiteUrl);
    }

    setMergeTarget(assetId: number, fundReportId?: number, mergeTargetId?: number, dataset?: string): Observable<AssetData> {
        return this.assetApiService.setMergeTargetAsset(assetId, fundReportId, mergeTargetId, dataset);
    }
}
